import { Button } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { OliGroup } from '../../pages/KitStaging/boards/groupByOli';
import { statusHex } from './TrackedCardTile';

/** Pair-level control rendered on the right of the card body.
 *  - `move`    (Cart, matched): one-tap "Move to JSA" → move_pair(initiate).
 *  - `pending` (Cart, mid-move): "Pending → JSA" badge + Undo → move_pair(undo).
 *  - `confirm` (JSA, mid-move): "Confirm placement" + Undo → move_pair(confirm/undo).
 *  Kit + material are always moved together on one shelf (deterministic). */
export type CardPairControl =
	| { mode: 'move'; onMove: () => void; loading?: boolean }
	| { mode: 'pending'; onUndo: () => void; loading?: boolean }
	| { mode: 'confirm'; onConfirm: () => void; onUndo: () => void; loading?: boolean }
	// Site-mismatch pair on a JSA shelf: one tap reroutes kit+material together
	// to a serving shelf (then the normal pending → confirm flow finishes it).
	| { mode: 'reroute'; onReroute: () => void; loading?: boolean };

/** Right-edge pair control: one card, one action for kit+material together. */
function PairControlPanel({ pair }: { pair: CardPairControl }) {
	return (
		<div
			onClick={(e) => e.stopPropagation()}
			style={{
				flexShrink: 0,
				width: 148,
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
				justifyContent: 'center',
				gap: 6,
				padding: '8px 10px',
				borderLeft: '1px dashed #e5e7eb',
			}}
		>
			{pair.mode === 'move' && (
				<Button type="primary" block loading={pair.loading} onClick={() => pair.onMove()} style={{ height: 44, fontWeight: 600, fontSize: 13 }}>
					Move to JSA
				</Button>
			)}
			{pair.mode === 'reroute' && (
				<>
					<div
						style={{
							textAlign: 'center',
							fontSize: 10,
							fontWeight: 700,
							letterSpacing: 0.4,
							color: '#cf1322',
							background: '#fff1f0',
							border: '1px solid #ffa39e',
							borderRadius: 4,
							padding: '3px 4px',
						}}
					>
						SITE MISMATCH
					</div>
					<Button danger type="primary" block loading={pair.loading} onClick={() => pair.onReroute()} style={{ height: 44, fontWeight: 600, fontSize: 12 }}>
						Reroute pair → JSA
					</Button>
				</>
			)}
			{pair.mode === 'pending' && (
				<>
					<div
						style={{
							textAlign: 'center',
							fontSize: 10,
							fontWeight: 700,
							letterSpacing: 0.4,
							color: '#d48806',
							background: '#fffbe6',
							border: '1px solid #ffe58f',
							borderRadius: 4,
							padding: '3px 4px',
						}}
					>
						PENDING → JSA
					</div>
					<Button block disabled={pair.loading} onClick={() => pair.onUndo()} style={{ height: 38, fontWeight: 600 }}>
						Undo
					</Button>
				</>
			)}
			{pair.mode === 'confirm' && (
				<>
					<Button type="primary" block loading={pair.loading} onClick={() => pair.onConfirm()} style={{ height: 40, fontWeight: 600, fontSize: 12 }}>
						Confirm placement
					</Button>
					<Button block disabled={pair.loading} onClick={() => pair.onUndo()} style={{ height: 38, fontWeight: 600 }}>
						Undo
					</Button>
				</>
			)}
		</div>
	);
}

/** Build a link to an OLI's detail page inside Fastlane. The boards live in
 *  the Fastlane app itself, so this is a same-app relative path to the
 *  `orders/line-items/:id` route (id = OLI UUID). Returns null when the uuid
 *  is missing (e.g. a card whose OLI hasn't synced a uuid yet). */
function erpOliUrl(uuid: string | null | undefined): string | null {
	if (!uuid) return null;
	return `/orders/line-items/${uuid}/`;
}

const AREA_LABEL: Record<string, string> = {
	unassigned: 'Unassigned',
	cart: 'Cart',
	msa: 'MSA',
	jsa: 'JSA',
	dekit: 'Dekit',
};

const CARD_HEIGHT = 130;
const BAR_WIDTH = 54;

const formatRuntime = (minutes: number | null): string => {
	if (minutes == null) return '—';
	const h = Math.floor(minutes / 60);
	const m = minutes % 60;
	return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatDate = (iso: string | null): string => {
	if (!iso) return '—';
	const d = new Date(iso);
	const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
	const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
	return `${days[d.getDay()]}, ${months[d.getMonth()]}-${d.getDate()}`;
};

function ItemCell({
	label,
	state,
	elsewhere,
}: {
	label: string;
	state: 'ready' | 'partial' | 'missing' | 'absent' | 'picked';
	elsewhere?: string | null;
}) {
	const bg =
		state === 'ready'
			? '#52c41a'
			: state === 'partial'
				? '#faad14'
				: state === 'picked'
					? '#e6fffb'
					: state === 'absent'
						? '#bfbfbf'
						: '#d1d5db';
	const fg = state === 'picked' ? '#08979c' : '#fff';
	const symbol =
		state === 'ready'
			? '✓'
			: state === 'partial'
				? '~'
				: state === 'picked'
					? '↑'
					: state === 'absent'
						? '—'
						: '✗';
	return (
		<div
			style={{
				flex: 1,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background: bg,
				color: fg,
				fontWeight: 700,
				fontSize: 10,
				gap: 4,
				border: state === 'picked' ? '1px solid #87e8de' : undefined,
			}}
		>
			<span>
				{label} {symbol}
			</span>
			{elsewhere && (
				<span
					style={{
						background: 'rgba(0,0,0,0.18)',
						padding: '1px 5px',
						borderRadius: 8,
						fontSize: 9,
						letterSpacing: 0.3,
						fontWeight: 700,
					}}
				>
					→ {elsewhere}
				</span>
			)}
		</div>
	);
}

interface Props {
	group: OliGroup;
	currentArea: string;
	selected: boolean;
	onSelect: (g: OliGroup) => void;
	/** When set, the card renders in a pending-placement state for THIS card id:
	 *  yellow background + inline Confirm / Undo buttons. */
	pendingForCardId?: number | null;
	onConfirmPending?: (cardId: number) => void;
	onUndoPending?: (cardId: number) => void;
	/** Pair-level control (move/pending/confirm) rendered on the card's right
	 *  edge. Drives the one-tap kit+material → JSA flow. */
	pair?: CardPairControl;
}

/** A combined 130px tile showing one OLI's kit + material cards with KIT/MAT indicators. */
export function OliCardTile({
	group,
	currentArea,
	selected,
	onSelect,
	pendingForCardId,
	onConfirmPending,
	onUndoPending,
	pair,
}: Props) {
	const here =
		group.kitCard?.current_area === currentArea
			? group.kitCard
			: group.materialCard?.current_area === currentArea
				? group.materialCard
				: (group.kitCard ?? group.materialCard);

	const job = group.job ?? null;
	const isCancelled = here?.status === 'cancelled';
	const isOnHold = here?.status === 'on_hold';
	const isSiteMismatch = here?.status === 'site_mismatch';
	const isTaskMismatch = here?.status === 'task_status_mismatch';
	const isSetupPending = here?.status === 'setup_pending';
	const isIssue = here?.status === 'issue';
	const pairPending = pair != null && pair.mode !== 'move';
	const isPending = pendingForCardId != null || pairPending;

	// A setup_pending card is physically at the machine; badge it by its actual
	// machine phase rather than a stale "Setup Pending" once setup advances.
	// Active phases are coloured (setup-started amber, machining blue); waiting
	// phases stay grey:
	//   machining in progress -> MACHINING   (blue, active)
	//   setup completed        -> SETUP DONE  (grey, waiting to machine)
	//   setup in progress      -> SETUP STARTED (amber, active)
	//   otherwise              -> SETUP PENDING (grey, awaiting setup advance)
	const setupTreatment = !isSetupPending
		? null
		: job?.machining_status === 'in_progress'
			? { border: '#1890ff', bg: '#e6f7ff', chip: '#1890ff', label: 'MACHINING' }
			: job?.machine_setup_status === 'completed'
				? { border: '#595959', bg: '#f0f0f0', chip: '#595959', label: 'SETUP DONE' }
				: job?.machine_setup_status === 'in_progress'
					? { border: '#faad14', bg: '#fffbe6', chip: '#d48806', label: 'SETUP STARTED' }
					: { border: '#595959', bg: '#f0f0f0', chip: '#595959', label: 'SETUP PENDING' };

	const mgColor = job?.machine_group?.color || '#8c8c8c';
	const mgName = job?.machine_group?.name || '—';
	// Soft-hidden cards are physically at the machine even though their
	// stored current_area may still read JSA/CART/etc. Surface that
	// truthfully in the area strip.
	const areaLabel = here?.is_soft_hidden
		? 'MACHINE'
		: (AREA_LABEL[here?.current_area ?? currentArea] ?? (here?.current_area ?? currentArea)).toUpperCase();
	// Priority rank is per-OLI; either paired card carries it.
	const priority = group.kitCard?.priority ?? group.materialCard?.priority ?? null;

	// Status -> visual treatment. Same pattern: thick coloured border, tinted
	// background, inline badge next to part name (rendered below).
	// The PENDING treatment (yellow) trumps the other status treatments so
	// the operator sees the awaiting-confirmation state plainly.
	const treatment = isPending
		? { border: '#faad14', bg: '#fffbe6', chip: '#d48806', label: 'AWAITING CONFIRM' }
		: isCancelled
			? { border: '#ff4d4f', bg: '#fff1f0', chip: '#cf1322', label: 'CANCELLED' }
			: isSiteMismatch
				? { border: '#ff4d4f', bg: '#fff1f0', chip: '#cf1322', label: 'SITE MISMATCH' }
				: isTaskMismatch
					? { border: '#faad14', bg: '#fffbe6', chip: '#d48806', label: 'TASK MISMATCH' }
					: isOnHold
						? { border: '#722ed1', bg: '#f9f0ff', chip: '#722ed1', label: 'ON HOLD' }
						: isSetupPending
							? setupTreatment
							: isIssue
								? { border: '#faad14', bg: '#fffbe6', chip: '#d48806', label: 'ISSUE' }
								: null;
	const borderColor = treatment?.border ?? '#e5e7eb';
	const borderWidth = treatment ? 2 : 1.5;
	const bg = treatment?.bg ?? '#fefefe';

	return (
		<div
			className="rounded-lg overflow-hidden cursor-pointer select-none flex flex-col"
			style={{
				height: CARD_HEIGHT,
				backgroundColor: bg,
				border: `${borderWidth}px solid ${borderColor}`,
				outline: selected ? '2px solid #1890ff' : undefined,
				position: 'relative',
			}}
			tabIndex={0}
			role="button"
			onClick={() => onSelect(group)}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					onSelect(group);
				}
			}}
		>
			{/* TOP SECTION: split bar + body */}
			<div className="flex flex-1 min-h-0">
				{/* LEFT SPLIT BAR */}
				<div className="flex flex-col flex-shrink-0" style={{ width: BAR_WIDTH }}>
					{/* Top half: machine group color + name */}
					<div
						className="flex items-center justify-center text-center px-1 flex-1"
						style={{ backgroundColor: mgColor }}
					>
						<span className="font-bold text-white leading-tight" style={{ fontSize: 11 }}>
							{mgName}
						</span>
					</div>
					{/* Bottom half: status color + priority badge + area label */}
					<div
						className="flex flex-col items-center justify-center text-center px-1 flex-1 gap-1"
						style={{ backgroundColor: statusHex(here?.status ?? 'waiting_both') }}
					>
						{priority != null && (
							<div
								className="flex items-center justify-center rounded-full font-black text-white"
								style={{
									backgroundColor: mgColor,
									fontSize: 16,
									width: 26,
									height: 26,
									border: '2px solid rgba(255,255,255,0.5)',
								}}
							>
								{priority}
							</div>
						)}
						<span
							className="font-bold text-white"
							style={{ fontSize: 10, writingMode: 'vertical-rl', letterSpacing: 1 }}
						>
							{areaLabel}
						</span>
					</div>
				</div>

				{/* MAIN BODY */}
				<div className="flex-1 min-w-0 px-2 py-1.5 flex flex-col justify-between">
					<div className="flex gap-1.5">
						<div
							className="flex-shrink-0 rounded bg-gray-100 flex items-center justify-center overflow-hidden border border-gray-200/60"
							style={{ width: 46, height: 46 }}
						>
							{job?.thumbnail ? (
								<img src={job.thumbnail} alt={job.part_name} className="w-full h-full object-cover" />
							) : (
								<InboxOutlined style={{ fontSize: 20, color: '#d1d5db' }} />
							)}
						</div>
						<div className="flex-1 min-w-0">
							<div className="font-bold text-gray-900 truncate" style={{ fontSize: 13 }}>
								{job?.job_number || `OLI #${group.oli}`}
							</div>
							<div
								className="leading-tight"
								style={{
									fontSize: 11,
									display: 'flex',
									alignItems: 'center',
									gap: 6,
									minWidth: 0,
								}}
							>
								{(() => {
									const href = erpOliUrl(job?.oli_uuid);
									const text = job?.part_name || '';
									if (!href || !text) {
										return (
											<span className="truncate" style={{ color: '#404040', flex: 1, minWidth: 0 }}>
												{text}
											</span>
										);
									}
									return (
										<a
											href={href}
											target="_blank"
											rel="noopener noreferrer"
											onClick={(e) => e.stopPropagation()}
											title={`Open OLI #${group.oli} in Fastlane`}
											className="truncate"
											style={{ color: '#1890ff', flex: 1, minWidth: 0, textDecoration: 'underline' }}
										>
											{text}
										</a>
									);
								})()}
								{treatment && (
									<span
										style={{
											flexShrink: 0,
											background: treatment.chip,
											color: '#fff',
											fontSize: 9,
											fontWeight: 700,
											letterSpacing: 0.5,
											padding: '2px 6px',
											borderRadius: 4,
											lineHeight: 1,
										}}
									>
										{treatment.label}
									</span>
								)}
							</div>
							<div className="text-gray-500 truncate" style={{ fontSize: 10 }}>
								{job?.quantity ?? '—'} pcs · {job?.material || ''}
								{job?.finish ? ` · ${job.finish}` : ''}
							</div>
							{job?.material_partial && (
								<div style={{ fontSize: 9, fontWeight: 700, color: '#faad14' }}>◐ Partial material</div>
							)}
						</div>
					</div>

					<div className="flex justify-between items-end mt-1">
						<div>
							<div className="text-gray-400 uppercase" style={{ fontSize: 8 }}>
								Start
							</div>
							<div className="text-gray-700 font-medium" style={{ fontSize: 11 }}>
								{formatDate(job?.scheduled_start ?? null)}
							</div>
						</div>
						<div className="text-right">
							<div className="text-gray-400 uppercase" style={{ fontSize: 8 }}>
								Mach. Time
							</div>
							<div className="text-gray-700 font-medium" style={{ fontSize: 11 }}>
								{formatRuntime(job?.est_machine_minutes ?? null)}
							</div>
						</div>
					</div>
				</div>
				{pair && <PairControlPanel pair={pair} />}
			</div>

			{/* BOTTOM STRIP: when this card is awaiting placement confirmation,
          replace the readiness indicators with CONFIRM / UNDO actions —
          that's the operator's only meaningful next move on this card. */}
			{isPending && pendingForCardId != null ? (
				<div className="flex flex-shrink-0" style={{ height: 26 }}>
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onConfirmPending?.(pendingForCardId);
						}}
						style={{
							flex: 1,
							border: 'none',
							cursor: 'pointer',
							background: '#52c41a',
							color: '#fff',
							fontWeight: 700,
							fontSize: 11,
							letterSpacing: 0.5,
						}}
					>
						CONFIRM PLACED
					</button>
					<div style={{ width: 1, backgroundColor: '#fff' }} />
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onUndoPending?.(pendingForCardId);
						}}
						style={{
							flex: 1,
							border: 'none',
							cursor: 'pointer',
							background: '#bfbfbf',
							color: '#fff',
							fontWeight: 700,
							fontSize: 11,
							letterSpacing: 0.5,
						}}
					>
						UNDO
					</button>
				</div>
			) : (
				<div className="flex flex-shrink-0" style={{ height: 22 }}>
					<ItemCell
						label="KIT"
						state={
							!group.kitCard
								? group.job?.kit_at_machine
									? 'ready'
									: 'absent'
								: group.kitCard.picked_up
									? 'picked'
									: group.job?.kit_ready
										? 'ready'
										: 'missing'
						}
						elsewhere={
							// Soft-hidden always reads as MACHINE regardless of stored area —
							// the card is physically at the machine.
							(group.kitCard && group.kitCard.is_soft_hidden) || (!group.kitCard && group.job?.kit_at_machine)
								? 'MACHINE'
								: group.kitCard && group.kitCard.current_area !== currentArea
									? (AREA_LABEL[group.kitCard.current_area] ?? group.kitCard.current_area).toUpperCase()
									: null
						}
					/>
					<div style={{ width: 1, backgroundColor: '#fff' }} />
					<ItemCell
						label="MAT"
						state={
							!group.materialCard
								? group.job?.material_at_machine
									? 'ready'
									: 'absent'
								: group.materialCard.picked_up
									? 'picked'
									: group.job?.material_ready
										? 'ready'
										: group.job?.material_partial
											? 'partial'
											: 'missing'
						}
						elsewhere={
							(group.materialCard && group.materialCard.is_soft_hidden) ||
							(!group.materialCard && group.job?.material_at_machine)
								? 'MACHINE'
								: group.materialCard && group.materialCard.current_area !== currentArea
									? (AREA_LABEL[group.materialCard.current_area] ?? group.materialCard.current_area).toUpperCase()
									: null
						}
					/>
				</div>
			)}
		</div>
	);
}
